import {
    createApi,
    fetchBaseQuery,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import { definitions, operations } from '@/apitypes';
import {
    CreateInvoiceType,
    EaccountingArticle,
    EaccountingInvoice,
    Paging,
    PaymentTerm,
    Price,
    Service,
    ServicePriceMapping,
    CodelockSettings,
    CreateInvoiceResponse,
    DefaultPriceArticle,
    UpdateEaccountingSettingsParams,
    EaccountingSettingsResponse,
    Article,
    Articles,
    GetArticlesQuery,
    CreateArticleQuery,
    ArticleMapping,
    GetArticleMappingsQuery,
    UpdateArticleMappingsQuery,
    CurrencyQuery,
    Currency,
    RebateCode,
    GetRebateCodesQuery,
    UpdateRebateCodeQuery,
    RebateCodeResponse,
    GetMailchimpAudienceQuery,
    MailchimpAudienceResponse,
    GetWebhookEndointsResponse,
    GetWebhookEndointsQuery,
    GetWebhookEventTypesResponse,
    GetWebhookLogsResponse,
    GetWebhookLogsQuery,
    GetWebhookEndpointAttemptsResponse,
    GetWebhookMessageAttemptsResponse,
    IncentiveResponse,
    SendReportMethodEnum,
    GetSimilarServicesResponse,
    Tag,
    TagScope,
    GetTagsQuery,
    TagsResponse,
    CreateTagBody,
    UpdateTagBody,
    AddTagsToBookingBody,
    RemoveTagFromBookingParams,
    AddTagsToCustomerBody,
    RemoveTagFromCustomerParams,
    AddTagsToTimeExceptionBody,
    RemoveTagFromTimeExceptionParams,
    CustomerSearchResult,
    CreateSummarizedInvoiceParams,
    SummarizedInvoiceCreateResponse,
    SummarizedInvoicesResponse,
    SummarizedInvoiceDetail,
    GetSummarizedInvoicesParams,
    PreviewSummarizedInvoiceParams,
    PreviewSummarizedInvoiceResponse,
    AxemaUserSyncJob,
    CreateAxemaUserSyncQuery,
    QueryAxemaUserSyncJobsQuery,
    QueryAxemaUserSyncJobsResponse,
    CompanyFeatureFlagResponse,
    ApiKeyQueryResponse,
    GetApiKeysQuery,
    ApiKey,
    CreateApiKeyBody,
} from '@/types';
import { getXlanguageCode } from '@/utils/i18n';
import keycloak from '@/keycloak';
import { format } from 'date-fns';

export type User = definitions['CurrentUserQueryResponse'];
export type WidgetConfigurationResponse = definitions['WidgetConfigurationResponse'];
export type WidgetConfigurationSchemaResponse = definitions['WidgetConfigurationSchemaResponse'];
export interface WidgetSchemaQueryArg {
    version?: number;
}
export type CreateWidgetConfigurationBody = definitions['CreateWidgetConfiguration'];
export type UpdateWidgetConfigurationBody = definitions['UpdateWidgetConfiguration'];

// Endpoints whose cached queries should be persisted across reloads.
// Use this for resource heavy API calls (i.e AI enpoints)
// This is used by the redux-persist transform in src/store.tsx.
export const PERSISTED_BM_API_ENDPOINTS = ['getSimilarServices'] as const;

const transformErrorResponse = (
    apiResponse: FetchBaseQueryError,
    meta: FetchBaseQueryMeta | undefined
) => {
    const method = meta?.request.method;
    const url = meta?.request.url;
    const requestData = meta?.request.body;
    return {
        ...apiResponse,
        method,
        url,
        requestData,
        correlationId: meta?.response?.headers?.get('x-correlation-id'),
    };
};

function fixSchemaMojibake(s: string): string {
    try {
        return decodeURIComponent(escape(s));
    } catch {
        return s;
    }
}

function fixSchemaEncoding(value: unknown): unknown {
    if (typeof value === 'string') return fixSchemaMojibake(value);
    if (Array.isArray(value)) return value.map(fixSchemaEncoding);
    if (value !== null && typeof value === 'object') {
        const result: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
            result[k] = fixSchemaEncoding(v);
        }
        return result;
    }
    return value;
}

export const apiService = createApi({
    reducerPath: 'bmApi',
    tagTypes: [
        'ServicePriceMappings',
        'Invoices',
        'Settings',
        'Auth',
        'article',
        'articles',
        'articleMappings',
        'rebateCode',
        'rebateCodes',
        'mailchimp',
        'webhooks',
        'similarServices',
        'tags',
        'summarizedInvoices',
        'axemaUserSyncJobs',
        'widgetConfigurations',
        'featureFlags',
        'googleMeetStatus',
        'apiKeys',
    ],
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.REACT_APP_API_BASE_URL,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_API_KEY,
            'x-language': getXlanguageCode(),
        },
        prepareHeaders(headers) {
            const bearerToken = keycloak?.token;
            const sessionId = keycloak?.sessionId;

            if (bearerToken && sessionId) {
                headers.set('Authorization', `Bearer ${bearerToken}`);
                headers.set('X-Ss-Id', `${sessionId}`);
            }

            return headers;
        },
    }),
    endpoints: (builder) => {
        return {
            getEaccountingArticles: builder.query<EaccountingArticle[], void>({
                query: () => 'eaccounting/articles',
                providesTags: ['ServicePriceMappings'],
                transformErrorResponse,
            }),
            searchEaccountingArticles: builder.query<
                EaccountingArticle[],
                { Search: string; Take?: number }
            >({
                query: ({ Search, Take = 25 }) => ({
                    url: 'eaccounting/articles',
                    params: { Search, Take },
                }),
                transformErrorResponse,
            }),
            getEaccountingArticle: builder.query<EaccountingArticle, string>({
                query: (articleId) => `eaccounting/articles/${articleId}`,
                transformErrorResponse,
            }),
            getEaccountingPriceMappings: builder.query<
                ServicePriceMapping[],
                { CompanyId: string }
            >({
                query: ({ CompanyId }) => ({
                    url: '/services/prices/mappings',
                    method: 'get',
                    params: {
                        CompanyId,
                    },
                }),
                providesTags: ['ServicePriceMappings'],
                transformErrorResponse,
            }),
            getDefaultEaccountingPriceMappings: builder.query<
                DefaultPriceArticle[],
                { CompanyId: string }
            >({
                query: ({ CompanyId }) => ({
                    url: '/eaccounting/defaultarticles',
                    method: 'get',
                    params: {
                        CompanyId,
                    },
                }),
                providesTags: ['ServicePriceMappings'],
                transformErrorResponse,
            }),
            updateEaccountingSettings: builder.mutation<
                DefaultPriceArticle[],
                UpdateEaccountingSettingsParams
            >({
                query: (data) => ({
                    url: '/eaccounting/settings',
                    method: 'put',
                    body: data,
                }),
                invalidatesTags: ['Settings'],
                transformErrorResponse,
            }),
            getEaccountingSettings: builder.query<
                EaccountingSettingsResponse,
                { CompanyId?: string }
            >({
                query: ({ CompanyId }) => ({
                    url: '/eaccounting/settings',
                    method: 'get',
                    params: {
                        CompanyId,
                    },
                }),
                providesTags: ['Settings'],
                transformErrorResponse,
            }),
            getEaccountingPrices: builder.query<
                Price[],
                operations['ServiceQuery_Get']['parameters']['query'] | void
            >({
                transformResponse: (res: Paging<Service>) =>
                    res.Results.map((s) =>
                        s.Prices.map((p) => ({ ...p, _serviceName: s.Name }))
                    ).flat(),
                query: (payload = {} as operations['ServiceQuery_Get']['parameters']['query']) => ({
                    url: '/services',
                    method: 'get',
                    params: {
                        IncludePrices: true,
                        ...payload,
                    },
                }),
                providesTags: ['ServicePriceMappings'],
                transformErrorResponse,
            }),
            getEaccountingPaymentTerms: builder.query<PaymentTerm[], {} | void>({
                query: () => ({
                    url: '/eaccounting/termsofpayments',
                    method: 'get',
                }),
            }),
            getEaccountingNotes: builder.query<any[], {} | void>({
                query: () => ({
                    url: '/eaccounting/notes',
                    method: 'get',
                }),
                transformErrorResponse,
            }),
            getEaccountingCustomers: builder.query<any[], {} | void>({
                query: () => ({
                    url: '/eaccounting/customers',
                    method: 'get',
                }),
                transformErrorResponse,
            }),
            getEaccountingInvoices: builder.query<
                EaccountingInvoice[],
                { BookingId?: number } | void
            >({
                query: (payload = {}) => ({
                    url: '/eaccounting/invoices',
                    method: 'get',
                    params: {
                        ...payload,
                    },
                }),
                providesTags: ['Invoices'],
                transformErrorResponse,
            }),
            getEaccountingInvoiceDrafts: builder.query<
                EaccountingInvoice[],
                { BookingId?: number } | void
            >({
                query: (payload = {}) => ({
                    url: '/eaccounting/invoicedrafts',
                    method: 'get',
                    params: {
                        ...payload,
                    },
                }),
                providesTags: ['Invoices'],
                transformErrorResponse,
            }),
            createEaccountingInvoice: builder.mutation<CreateInvoiceResponse, CreateInvoiceType>({
                query: (data) => ({
                    url: '/eaccounting/invoice',
                    method: 'post',
                    body: data,
                }),
                invalidatesTags: ['Invoices'],
                transformErrorResponse,
            }),
            createEaccountingInvoiceDraft: builder.mutation<
                CreateInvoiceResponse,
                CreateInvoiceType
            >({
                query: (data) => ({
                    url: '/eaccounting/invoicedraft',
                    method: 'post',
                    body: data,
                }),
                invalidatesTags: ['Invoices'],
                transformErrorResponse,
            }),
            updateEaccountingPriceArticleMappings: builder.mutation<
                operations['EAccountingUpdatePricesMappingarticlemappings_Create']['responses']['200']['schema'],
                operations['EAccountingUpdatePricesMappingarticlemappings_Create']['parameters']['body']['body']
            >({
                query: (data) => ({
                    url: '/eaccounting/article/mappings',
                    method: 'put',
                    body: data,
                }),
                invalidatesTags: ['ServicePriceMappings'],
                transformErrorResponse,
            }),
            deletePriceArticleMapping: builder.mutation<void, string>({
                query: (id) => ({
                    url: `/services/prices/mappings/${id}`,
                    method: 'delete',
                }),
                invalidatesTags: ['ServicePriceMappings'],
                transformErrorResponse,
            }),
            getArticleMappings: builder.query<ArticleMapping[], GetArticleMappingsQuery>({
                query: (params) => ({
                    url: '/articles/mappings',
                    method: 'get',
                    params,
                }),
                providesTags: ['articleMappings'],
                transformErrorResponse,
            }),
            updateArticleMappings: builder.mutation<ArticleMapping[], UpdateArticleMappingsQuery>({
                query: (data) => ({
                    url: '/articles/mappings',
                    method: 'put',
                    body: data,
                }),
                invalidatesTags: ['articleMappings'],
                transformErrorResponse,
            }),
            deleteArticleMapping: builder.mutation<void, string>({
                query: (id) => ({
                    url: `/articles/mappings/${id}`,
                    method: 'delete',
                }),
                invalidatesTags: ['articleMappings'],
                transformErrorResponse,
            }),
            checkEaccountingAuth: builder.query<any, {} | void>({
                query: () => ({
                    url: '/eaccounting/check',
                    method: 'get',
                }),
                providesTags: ['Auth'],
            }),
            eaccountingLogout: builder.mutation<void, void>({
                query: () => ({
                    url: '/eaccounting/logout',
                    method: 'post',
                }),
                invalidatesTags: ['Auth', 'Invoices', 'ServicePriceMappings', 'Settings'],
                transformErrorResponse,
            }),
            getUsers: builder.query<any, {} | void>({
                // transformResponse: (res: Paging<Service>) => res.Results.map(s => s.Prices).flat(),
                query: () => ({
                    url: '/users',
                    method: 'get',
                }),
                transformErrorResponse,
            }),
            getCodelockSettings: builder.query<CodelockSettings, {} | void>({
                query: () => ({
                    url: '/codelock/settings',
                    method: 'get',
                    params: {
                        IncludeCodeLockSystemOptions: true,
                    },
                }),
                transformErrorResponse,
            }),
            // curl 'https://api-staging.bookmore.com/codelock/settings?IncludeCodeLockSystemOptions=true'
            resyncCodelock: builder.mutation<any, { bookingId: string; companyId: string }>({
                query: ({ bookingId, companyId }) => ({
                    url: `/codelock/resync/${bookingId}`,
                    method: 'post',
                    body: {
                        CompanyId: companyId,
                    },
                }),
                transformErrorResponse,
            }),
            sendForApproval: builder.mutation<any, { companyId: string }>({
                query: ({ companyId }) => ({
                    url: `/companies/${companyId}/sendforapproval`,
                    method: 'put',
                }),
                transformErrorResponse,
            }),
            getArticles: builder.query<Articles, GetArticlesQuery>({
                query: (params) => ({
                    url: '/articles',
                    method: 'get',
                    params,
                }),
                providesTags: ['articles'],
                transformErrorResponse,
            }),
            updateArticle: builder.mutation<Article, CreateArticleQuery & { Id: string }>({
                query: (data) => ({
                    url: `/articles/${data.Id}`,
                    method: 'put',
                    body: data,
                }),
                invalidatesTags: ['articles'],
                transformErrorResponse,
            }),
            createArticle: builder.mutation<Article, CreateArticleQuery>({
                query: (data) => ({
                    url: '/articles',
                    method: 'post',
                    body: data,
                }),
                transformErrorResponse,
                invalidatesTags: ['articles'],
            }),
            deleteArticle: builder.mutation<void, { id: string }>({
                query: (params) => ({
                    url: `/articles/${params.id}`,
                    method: 'delete',
                }),
                invalidatesTags: ['articles'],
                transformErrorResponse,
            }),
            getCurrencies: builder.query<Currency[], CurrencyQuery>({
                query: (params) => ({
                    url: '/currencies',
                    method: 'get',
                    params: {
                        Active: true,
                        ...params,
                    },
                }),
                transformErrorResponse,
            }),
            getRebateCodes: builder.query<RebateCodeResponse, GetRebateCodesQuery>({
                query: (params) => ({
                    url: '/rebatecodes',
                    method: 'get',
                    params,
                }),
                providesTags: ['rebateCodes'],
                transformErrorResponse,
            }),
            getMailchimpAudience: builder.query<
                MailchimpAudienceResponse,
                GetMailchimpAudienceQuery
            >({
                query: (params) => ({
                    url: '/mailchimp/audience/company',
                    method: 'get',
                    params,
                }),

                providesTags: ['mailchimp'],
                transformErrorResponse,
            }),
            getWebhookEndpoints: builder.query<GetWebhookEndointsResponse, GetWebhookEndointsQuery>(
                {
                    query: (params) => ({
                        url: '/webhook/endpoints',
                        method: 'get',
                        params,
                    }),

                    providesTags: ['webhooks'],
                    transformErrorResponse,
                }
            ),
            getWebhookLogs: builder.query<GetWebhookLogsResponse, GetWebhookLogsQuery>({
                query: (params) => ({
                    url: '/webhook/messages',
                    method: 'get',
                    params,
                }),
                providesTags: ['webhooks'],
                transformErrorResponse,
            }),
            getWebhookEventTypes: builder.query<GetWebhookEventTypesResponse, any>({
                query: (params) => ({
                    url: '/webhook/eventtypes',
                    method: 'get',
                    params,
                }),

                providesTags: ['webhooks'],
                transformErrorResponse,
            }),
            getWebhookEndpointAttempts: builder.query<
                GetWebhookEndpointAttemptsResponse,
                { endpointId: string }
            >({
                query: ({ endpointId }) => ({
                    url: `/webhook/endpoints/${endpointId}/attempts`,
                    method: 'get',
                }),
                providesTags: ['webhooks'],
                transformErrorResponse,
            }),
            getWebhookMessageAttempts: builder.query<
                GetWebhookMessageAttemptsResponse,
                { messageId: string }
            >({
                query: ({ messageId }) => ({
                    url: `/webhook/messages/${messageId}/attempts`,
                    method: 'get',
                }),
                providesTags: ['webhooks'],
                transformErrorResponse,
            }),
            updateRebateCode: builder.mutation<void, { id: string }>({
                query: (params) => ({
                    url: `/rebatecodes/${params.id}`,
                    method: 'put',
                }),
                invalidatesTags: ['rebateCodes'],
                transformErrorResponse,
            }),
            createRebateCode: builder.mutation<RebateCode, UpdateRebateCodeQuery>({
                query: (data) => ({
                    url: '/rebatecodes',
                    method: 'post',
                    body: data,
                }),
                invalidatesTags: ['rebateCodes'],
                transformErrorResponse,
            }),
            deleteRebateCode: builder.mutation<void, { id: string }>({
                query: (params) => ({
                    url: `/rebatecodes/${params.id}`,
                    method: 'delete',
                }),
                invalidatesTags: ['rebateCodes'],
                transformErrorResponse,
            }),
            getIncentives: builder.query<IncentiveResponse, { companyId: string }>({
                query: ({ companyId }) => ({
                    url: `/companies/${companyId}/incentives`,
                }),
            }),
            getArticleReceipt: builder.query<
                any,
                {
                    internalRefenceId: number;
                    sendMethod: SendReportMethodEnum;
                    articleTypeId: number;
                }
            >({
                query: ({ internalRefenceId, sendMethod, articleTypeId }) => ({
                    url: `/articles/${internalRefenceId}/reports`,
                    params: { SendReceiptMethod: sendMethod, ArticleTypeId: articleTypeId },
                    responseHandler: async (response) => {
                        // window.location.assign(window.URL.createObjectURL(await response.blob()))
                        const blob = await response.blob();
                        const hiddenElement = document.createElement('a');
                        const blobPDF = window.URL.createObjectURL(blob);
                        hiddenElement.href = blobPDF;
                        hiddenElement.target = '_blank';
                        hiddenElement.download = `${format(new Date(), 'P')}_receipt.pdf`;
                        hiddenElement.click();

                        return blob;
                    },
                    cache: 'no-cache',
                }),
            }),
            getServices: builder.query<definitions['ServiceQueryResponse'][], void>({
                query: () => ({
                    url: '/services',
                    method: 'get',
                }),
                transformResponse: (res: definitions['QueryResponse_ServiceQueryResponse_']) =>
                    res.Results ?? [],
                transformErrorResponse,
            }),
            getGoogleMeetAuthUrl: builder.query<
                { AuthUrl: string },
                { resourceId: number; companyId?: string }
            >({
                query: ({ resourceId, companyId }) => ({
                    url: `/google/meet/auth/${resourceId}`,
                    method: 'get',
                    params: companyId ? { CompanyId: companyId } : undefined,
                }),
                transformErrorResponse,
            }),
            getGoogleMeetStatus: builder.query<
                {
                    Connected: boolean;
                    GoogleAccountEmail: string | null;
                    CalendarId: string | null;
                    ConnectedSince: string | null;
                    NeedsReauth: boolean;
                },
                { resourceId: number; companyId?: string }
            >({
                query: ({ resourceId, companyId }) => ({
                    url: `/google/meet/status/${resourceId}`,
                    method: 'get',
                    params: companyId ? { CompanyId: companyId } : undefined,
                }),
                providesTags: (_result, _error, { resourceId }) => [
                    { type: 'googleMeetStatus', id: resourceId },
                ],
                transformErrorResponse,
            }),
            disconnectGoogleMeet: builder.mutation<
                void,
                { resourceId: number; companyId?: string }
            >({
                query: ({ resourceId, companyId }) => ({
                    url: `/google/meet/auth/${resourceId}`,
                    method: 'delete',
                    params: companyId ? { CompanyId: companyId } : undefined,
                }),
                invalidatesTags: (_result, _error, { resourceId }) => [
                    { type: 'googleMeetStatus', id: resourceId },
                ],
                transformErrorResponse,
            }),
            getSimilarServices: builder.query<GetSimilarServicesResponse, any>({
                query: (params) => ({
                    // /ai/generate-similar-services
                    url: '/ai/generate-similar-services',
                    method: 'post',
                    body: params,
                }),
                // Keep cached similar services data effectively forever.
                // It will only be refreshed when we explicitly invalidate
                // the `similarServices` tag (e.g. on POST /services).
                keepUnusedDataFor: Number.MAX_SAFE_INTEGER,
                providesTags: ['similarServices'],
                transformErrorResponse,
            }),
            getTags: builder.query<TagsResponse, GetTagsQuery>({
                query: (params) => ({
                    url: '/tags',
                    method: 'get',
                    params,
                }),
                providesTags: ['tags'],
                transformResponse: (response: TagsResponse) => ({
                    ...response,
                    Results: response.Results.map((tag) => ({
                        ...tag,
                        Scope:
                            typeof tag.Scope === 'string'
                                ? (tag.Scope as unknown as string) === 'Customer'
                                    ? TagScope.Customer
                                    : (tag.Scope as unknown as string) === 'TimeException'
                                    ? TagScope.TimeException
                                    : TagScope.Booking
                                : tag.Scope,
                    })),
                }),
                transformErrorResponse,
            }),
            createTag: builder.mutation<Tag, CreateTagBody>({
                query: (body) => ({
                    url: '/tags',
                    method: 'post',
                    body,
                }),
                invalidatesTags: ['tags'],
                transformErrorResponse,
            }),
            deleteTag: builder.mutation<Tag, { id: number; CompanyId?: string }>({
                query: ({ id }) => ({
                    url: `/tags/${id}`,
                    method: 'delete',
                }),
                invalidatesTags: ['tags'],
                transformErrorResponse,
            }),
            updateTag: builder.mutation<Tag, UpdateTagBody>({
                query: ({ Id, ...body }) => ({
                    url: `/tags/${Id}`,
                    method: 'put',
                    body,
                }),
                invalidatesTags: ['tags'],
                transformErrorResponse,
            }),
            addTagsToBooking: builder.mutation<any, AddTagsToBookingBody>({
                query: ({ BookingId, ...body }) => ({
                    url: `/bookings/${BookingId}/tags`,
                    method: 'post',
                    body,
                }),
                transformErrorResponse,
            }),
            removeTagFromBooking: builder.mutation<any, RemoveTagFromBookingParams>({
                query: ({ BookingId, TagId }) => ({
                    url: `/bookings/${BookingId}/tags/${TagId}`,
                    method: 'delete',
                }),
                transformErrorResponse,
            }),
            addTagsToCustomer: builder.mutation<any, AddTagsToCustomerBody>({
                query: ({ CustomerId, ...body }) => ({
                    url: `/customers/${CustomerId}/tags`,
                    method: 'post',
                    body,
                }),
                transformErrorResponse,
            }),
            removeTagFromCustomer: builder.mutation<any, RemoveTagFromCustomerParams>({
                query: ({ CustomerId, TagId }) => ({
                    url: `/customers/${CustomerId}/tags/${TagId}`,
                    method: 'delete',
                }),
                transformErrorResponse,
            }),
            addTagsToTimeException: builder.mutation<any, AddTagsToTimeExceptionBody>({
                query: ({ TimeExceptionId, ...body }) => ({
                    url: `/timeexceptions/${TimeExceptionId}/tags`,
                    method: 'post',
                    body,
                }),
                transformErrorResponse,
            }),
            removeTagFromTimeException: builder.mutation<any, RemoveTagFromTimeExceptionParams>({
                query: ({ TimeExceptionId, TagId }) => ({
                    url: `/timeexceptions/${TimeExceptionId}/tags/${TagId}`,
                    method: 'delete',
                }),
                transformErrorResponse,
            }),
            // Customer search (for summarized invoices)
            searchCustomers: builder.query<
                { Results: CustomerSearchResult[] },
                { search?: string } | void
            >({
                query: (params = {}) => ({
                    url: '/customers',
                    method: 'get',
                    params: { ...params, Take: 50 },
                }),
                transformErrorResponse,
            }),
            // Summarized Invoices
            previewSummarizedInvoice: builder.query<
                PreviewSummarizedInvoiceResponse,
                PreviewSummarizedInvoiceParams
            >({
                query: ({ BookingStatusIds, ...rest }) => ({
                    url: '/eaccounting/summarizedinvoice/preview',
                    method: 'get',
                    params: {
                        ...rest,
                        ...(BookingStatusIds?.length
                            ? { BookingStatusIds: BookingStatusIds.join(',') }
                            : {}),
                    },
                }),
                transformErrorResponse,
            }),
            createSummarizedInvoice: builder.mutation<
                SummarizedInvoiceCreateResponse,
                CreateSummarizedInvoiceParams
            >({
                query: (data) => ({
                    url: '/eaccounting/summarizedinvoice',
                    method: 'post',
                    body: data,
                }),
                invalidatesTags: ['summarizedInvoices'],
                transformErrorResponse,
            }),
            getSummarizedInvoices: builder.query<
                SummarizedInvoicesResponse,
                GetSummarizedInvoicesParams | void
            >({
                query: (params = {}) => ({
                    url: '/eaccounting/summarizedinvoices',
                    method: 'get',
                    params: { ...params },
                }),
                providesTags: ['summarizedInvoices'],
                transformErrorResponse,
            }),
            getSummarizedInvoice: builder.query<
                SummarizedInvoiceDetail,
                { Id: string; CompanyId?: string }
            >({
                query: ({ Id, ...params }) => ({
                    url: `/eaccounting/summarizedinvoice/${Id}`,
                    method: 'get',
                    params,
                }),
                providesTags: ['summarizedInvoices'],
                transformErrorResponse,
            }),
            getAxemaUserSyncJobs: builder.query<
                QueryAxemaUserSyncJobsResponse,
                QueryAxemaUserSyncJobsQuery | void
            >({
                query: (params = {}) => ({
                    url: '/codelock/axema/usersync',
                    method: 'get',
                    params: params ?? {},
                }),
                providesTags: ['axemaUserSyncJobs'],
                transformErrorResponse,
            }),
            startAxemaUserSync: builder.mutation<AxemaUserSyncJob, CreateAxemaUserSyncQuery | void>(
                {
                    query: (body = {}) => ({
                        url: '/codelock/axema/usersync',
                        method: 'post',
                        body: body ?? {},
                    }),
                    invalidatesTags: ['axemaUserSyncJobs'],
                    transformErrorResponse,
                }
            ),
            getWidgetConfigurationSchema: builder.query<WidgetConfigurationSchemaResponse, WidgetSchemaQueryArg | void>({
                query: (arg) => ({
                    url: '/widget/configurationschema',
                    method: 'get',
                    params: arg?.version === undefined ? { Take: 1, OrderBy: '-Version' } : { Take: 1, Version: arg.version },
                }),
                transformResponse: (
                    res: definitions['QueryResponse_WidgetConfigurationSchemaResponse_']
                ) => {
                    const result = res.Results?.[0];
                    if (!result?.Schema) return result ?? {};
                    const schema = { ...result.Schema } as any;
                    if (typeof schema.properties === 'string')
                        schema.properties = JSON.parse(schema.properties);
                    if (typeof schema.$defs === 'string') schema.$defs = JSON.parse(schema.$defs);
                    if (typeof schema.required === 'string')
                        schema.required = JSON.parse(schema.required);
                    return { ...result, Schema: fixSchemaEncoding(schema) as typeof result.Schema };
                },
                transformErrorResponse,
            }),
            getWidgetConfigurations: builder.query<WidgetConfigurationResponse[], void>({
                query: () => ({
                    url: '/widget/configuration',
                    method: 'get',
                    params: { OrderBy: 'CreatedDate' },
                }),
                transformResponse: (
                    res: definitions['QueryResponse_WidgetConfigurationResponse_']
                ) => res.Results ?? [],
                providesTags: ['widgetConfigurations'],
                transformErrorResponse,
            }),
            createWidgetConfiguration: builder.mutation<
                WidgetConfigurationResponse,
                CreateWidgetConfigurationBody
            >({
                query: (body) => ({
                    url: '/widget/configuration',
                    method: 'post',
                    body,
                }),
                invalidatesTags: ['widgetConfigurations'],
                transformErrorResponse,
            }),
            updateWidgetConfiguration: builder.mutation<
                WidgetConfigurationResponse,
                UpdateWidgetConfigurationBody
            >({
                query: ({ Id, ...body }) => ({
                    url: `/widget/configuration/${Id}`,
                    method: 'put',
                    body: { Id, ...body },
                }),
                invalidatesTags: ['widgetConfigurations'],
                transformErrorResponse,
            }),
            deleteWidgetConfiguration: builder.mutation<void, { Id: string }>({
                query: ({ Id }) => ({
                    url: `/widget/configuration/${Id}`,
                    method: 'delete',
                }),
                invalidatesTags: ['widgetConfigurations'],
                transformErrorResponse,
            }),
            resetWidgetConfiguration: builder.mutation<WidgetConfigurationResponse, { Id: string }>({
                query: ({ Id }) => ({
                    url: `/widget/configuration/${Id}/reset`,
                    method: 'put',
                }),
                invalidatesTags: ['widgetConfigurations'],
                transformErrorResponse,
            }),
            setHomepageWidgetConfiguration: builder.mutation<WidgetConfigurationResponse, { Id: string }>({
                query: ({ Id }) => ({
                    url: `/widget/configuration/${Id}/sethomepage`,
                    method: 'put',
                }),
                invalidatesTags: ['widgetConfigurations'],
                transformErrorResponse,
            }),
            bumpWidgetConfigurationVersion: builder.mutation<
                WidgetConfigurationResponse,
                { Id: string; UseFactoryDefaults: boolean }
            >({
                query: ({ Id, UseFactoryDefaults }) => ({
                    url: `/widget/configuration/${Id}/bumpversion`,
                    method: 'put',
                    body: { UseFactoryDefaults },
                }),
                invalidatesTags: ['widgetConfigurations'],
                transformErrorResponse,
            }),
            getCompanyFeatureFlags: builder.query<
                CompanyFeatureFlagResponse[],
                { CompanyId: string }
            >({
                query: ({ CompanyId }) => ({
                    url: `/feature/${CompanyId}`,
                    method: 'get',
                }),
                transformResponse: (res: { Results?: CompanyFeatureFlagResponse[] }) =>
                    res.Results ?? [],
                providesTags: ['featureFlags'],
                transformErrorResponse,
            }),
            setCompanyFeatureFlag: builder.mutation<
                CompanyFeatureFlagResponse,
                { CompanyId: string; Id: number; IsEnabled: boolean }
            >({
                query: ({ CompanyId, Id, IsEnabled }) => ({
                    url: `/feature/${CompanyId}/${Id}`,
                    method: 'PUT',
                    body: { IsEnabled },
                }),
                invalidatesTags: ['featureFlags'],
                transformErrorResponse,
            }),
            getApiKeys: builder.query<ApiKeyQueryResponse, GetApiKeysQuery | void>({
                query: (params = {}) => ({
                    url: '/apikeys',
                    method: 'get',
                    params: params ?? {},
                }),
                providesTags: ['apiKeys'],
                transformErrorResponse,
            }),
            createApiKey: builder.mutation<ApiKey, CreateApiKeyBody | void>({
                query: (body = {}) => ({
                    url: '/apikeys',
                    method: 'post',
                    body: body ?? {},
                }),
                invalidatesTags: ['apiKeys'],
                transformErrorResponse,
            }),
        };
    },
});

// TODO optimize: skip eaccounting endpoints when eaccounting is not enabled

export const {
    useGetEaccountingArticlesQuery,
    useLazySearchEaccountingArticlesQuery,
    useGetEaccountingArticleQuery,
    useGetDefaultEaccountingPriceMappingsQuery,
    useGetEaccountingPriceMappingsQuery,
    useGetEaccountingPricesQuery,
    useGetEaccountingPaymentTermsQuery,
    useCreateEaccountingInvoiceMutation,
    useCreateEaccountingInvoiceDraftMutation,
    useGetEaccountingNotesQuery,
    useGetEaccountingCustomersQuery,
    useGetEaccountingInvoicesQuery,
    useCheckEaccountingAuthQuery,
    useGetUsersQuery,
    useResyncCodelockMutation,
    useGetCodelockSettingsQuery,
    useUpdateEaccountingSettingsMutation,
    useGetEaccountingSettingsQuery,
    useEaccountingLogoutMutation,
    useUpdateEaccountingPriceArticleMappingsMutation,
    useGetArticleMappingsQuery,
    useUpdateArticleMappingsMutation,
    useDeleteArticleMappingMutation,
    useSendForApprovalMutation,
    useUpdateArticleMutation,
    useGetArticlesQuery,
    useLazyGetArticlesQuery,
    useCreateArticleMutation,
    useDeleteArticleMutation,
    useGetCurrenciesQuery,
    useGetRebateCodesQuery,
    useLazyGetRebateCodesQuery,
    useUpdateRebateCodeMutation,
    useCreateRebateCodeMutation,
    useDeleteRebateCodeMutation,
    useGetMailchimpAudienceQuery,
    useGetWebhookEndpointsQuery,
    useGetWebhookEventTypesQuery,
    useGetWebhookLogsQuery,
    useGetWebhookEndpointAttemptsQuery,
    useGetWebhookMessageAttemptsQuery,
    useGetIncentivesQuery,
    useLazyGetIncentivesQuery,
    useGetArticleReceiptQuery,
    useLazyGetArticleReceiptQuery,
    useGetServicesQuery,
    useGetSimilarServicesQuery,
    useGetTagsQuery,
    useLazyGetTagsQuery,
    useCreateTagMutation,
    useDeleteTagMutation,
    useUpdateTagMutation,
    useAddTagsToBookingMutation,
    useRemoveTagFromBookingMutation,
    useAddTagsToCustomerMutation,
    useRemoveTagFromCustomerMutation,
    useAddTagsToTimeExceptionMutation,
    useRemoveTagFromTimeExceptionMutation,
    useLazySearchCustomersQuery,
    useLazyPreviewSummarizedInvoiceQuery,
    useCreateSummarizedInvoiceMutation,
    useGetSummarizedInvoicesQuery,
    useGetSummarizedInvoiceQuery,
    useGetAxemaUserSyncJobsQuery,
    useStartAxemaUserSyncMutation,
    useGetWidgetConfigurationSchemaQuery,
    useGetWidgetConfigurationsQuery,
    useCreateWidgetConfigurationMutation,
    useUpdateWidgetConfigurationMutation,
    useDeleteWidgetConfigurationMutation,
    useResetWidgetConfigurationMutation,
    useSetHomepageWidgetConfigurationMutation,
    useBumpWidgetConfigurationVersionMutation,
    useGetCompanyFeatureFlagsQuery,
    useSetCompanyFeatureFlagMutation,
    useLazyGetGoogleMeetAuthUrlQuery,
    useGetGoogleMeetStatusQuery,
    useDisconnectGoogleMeetMutation,
    useGetApiKeysQuery,
    useCreateApiKeyMutation,
} = apiService;

export default apiService;
